import {FC, ReactNode} from 'react';
import PropTypes from 'prop-types';
import {useTranslate, usePermissions, useSidebarState} from 'react-admin';
// @ts-ignore
import classnames from 'classnames';
import {useMediaQuery, Theme, List} from '@mui/material';
import {styled} from '@mui/material/styles';
import lodashGet from 'lodash/get';
import CustomMenuItemLink from '../customMenuItemLink/customMenuItemLink.component';
import CustomDashboardMenuItem from '../customDashboardMenuItem/customDashboardMenuItem.component';

// Icons
import UserIcon from '@mui/icons-material/People';
import PermitIcon from '@mui/icons-material/FactCheck';
import RiskTemplateIcon from '@mui/icons-material/Rule';
import MetadataIcon from '@mui/icons-material/Description';
import RiskIcon from '@mui/icons-material/WarningAmber';
import CategoryIcon from '@mui/icons-material/Category';
import ActionIcon from '@mui/icons-material/Checklist';
import MapIcon from '@mui/icons-material/Map';
import SubMenu from '../subMenu/subMenu.component';

export const MENU_WIDTH = 240;
export const CLOSED_MENU_WIDTH = 55;

const StyledMenu = styled(List, {shouldForwardProp: prop => prop !== 'sidebarOpen'})<{
  sidebarOpen: boolean;
}>(({theme, sidebarOpen}) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  marginTop: '0.5em',
  [theme.breakpoints.only('xs')]: {
    marginTop: 0,
  },
  [theme.breakpoints.up('md')]: {
    marginTop: '1.5em',
  },
  width: lodashGet(theme, sidebarOpen ? 'menu.width' : 'menu.closedWidth', sidebarOpen ? MENU_WIDTH : CLOSED_MENU_WIDTH),
}));

const Menu: FC<MenuProps> = props => {
  const {classes: classesOverride, className, dense, hasDashboard, ...rest} = props;

  const isXSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
  const [open] = useSidebarState();
  const {permissions} = usePermissions();

  const translate = useTranslate();

  return (
    <StyledMenu
      className={classnames(className)}
      sidebarOpen={open}
      {...rest}>
      {hasDashboard && <CustomDashboardMenuItem dense={dense} />}
      {permissions === 'admin' && (
        <CustomMenuItemLink to="/users" primaryText={translate('resources.users.name')} leftIcon={<UserIcon />} />
      )}
      {permissions === 'admin' && (
        <CustomMenuItemLink
          to="/operations"
          primaryText={translate('resources.operations.name')}
          leftIcon={<PermitIcon />}
        />
      )}
      {permissions === 'admin' && (
        <CustomMenuItemLink to="/map" primaryText="Karta" leftIcon={<MapIcon />} />
      )}
      {permissions === 'admin' && (
        <SubMenu
          text={translate('resources.risktemplates.name')}
          icon={<RiskTemplateIcon />}
          links={[
            {text: 'resources.risktemplates.meta', link: '/riskTemplates/1', icon: <MetadataIcon />},
            {text: 'resources.riskCategories.name', link: '/riskCategories', icon: <CategoryIcon />},
            {text: 'resources.risks.name', link: '/risks', icon: <RiskIcon />},
            {text: 'resources.actions.name', link: '/actions', icon: <ActionIcon />},
          ]}
          dense={dense}
          sidebarIsOpen={open}
          {...props}
        />
      )}
      {isXSmall}
    </StyledMenu>
  );
};

export interface MenuProps {
  classes?: object;
  className?: string;
  dense?: boolean;
  hasDashboard?: boolean;
  logout?: ReactNode;
}

Menu.propTypes = {
  classes: PropTypes.object,
  className: PropTypes.string,
  dense: PropTypes.bool,
  hasDashboard: PropTypes.bool,
  logout: PropTypes.element,
};

Menu.defaultProps = {};

export default Menu;
